import { Divider, Section, Stack } from '@cmg/design-system';
import { FormattedNumber } from '@cmg/i18n';
import { useState } from 'react';

import { useOfProceedsLabels } from '../../../../types/domain/filing/constants';
import {
  OfferingDetails_PublishedOffering_TermsFragment,
  OfferingDetails_PublishedOfferingFragment,
} from '../../../offering-side-panel/offering-details/graphql/__generated__';
import { KeyValuePair } from '../KeyValuePair';
import { PricePairs } from './components/price-pairs/PricePairs';
import { TermsDropdownButton } from './components/TermsDropdownButton';

type TermsSectionProps = {
  offeringType: OfferingDetails_PublishedOfferingFragment['type'];
  terms: OfferingDetails_PublishedOffering_TermsFragment;
  pricingCurrencyCode: OfferingDetails_PublishedOfferingFragment['pricingCurrencyCode'];
};

export function TermsSection({
  offeringType,
  terms,
  pricingCurrencyCode,
}: Readonly<TermsSectionProps>) {
  const [latestFilling, setLatestFiling] = useState(
    terms.filings.length ? terms.filings[terms.filings.length - 1] : undefined
  );

  const titleItems = latestFilling
    ? [
        <TermsDropdownButton
          key="terms-dropdown-button"
          terms={terms}
          latestFilling={latestFilling}
          setLatestFiling={setLatestFiling}
        />,
      ]
    : [];

  return (
    <Section title="Terms" titleItems={titleItems} data-testid="terms-section">
      <KeyValuePair
        label="Offering Size"
        value={
          <FormattedNumber
            value={latestFilling?.sizeInCurrency}
            format="currency"
            currency={pricingCurrencyCode}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
          />
        }
      />
      <PricePairs
        offeringType={offeringType}
        latestFilling={latestFilling}
        followOnPricing={terms.followOnPricing}
        pricingCurrencyCode={pricingCurrencyCode}
      />
      <Stack py={1}>
        <Divider />
      </Stack>
      <KeyValuePair
        label="Base Offering Shares"
        value={<FormattedNumber value={latestFilling?.totalSharesBaseOffering} format="integer" />}
      />
      <KeyValuePair
        label="Base Offering Shares/Primary"
        value={
          <FormattedNumber value={latestFilling?.primarySharesBaseOffering} format="integer" />
        }
      />
      <KeyValuePair
        label="Base Offering Shares/Secondary"
        value={
          <FormattedNumber value={latestFilling?.secondarySharesBaseOffering} format="integer" />
        }
      />
      <Stack py={1}>
        <Divider />
      </Stack>
      <KeyValuePair
        label="Ovlt Authorized"
        value={
          <FormattedNumber
            value={latestFilling?.notionalOverAllotmentAuthorized}
            format="currencyCompact"
            currency={pricingCurrencyCode}
          />
        }
      />
      <KeyValuePair
        label="Ovlt Authorized Shares"
        value={
          <FormattedNumber
            value={latestFilling?.totalSharesOverAllotmentAuthorized}
            format="integer"
          />
        }
      />
      <KeyValuePair
        label="Ovlt Authorized Shares/Primary"
        value={
          <FormattedNumber
            value={latestFilling?.primarySharesOverAllotmentAuthorized}
            format="integer"
          />
        }
      />
      <KeyValuePair
        label="Ovlt Authorized Shares/Secondary"
        value={
          <FormattedNumber
            value={latestFilling?.secondarySharesOverAllotmentAuthorized}
            format="integer"
          />
        }
      />
      <Stack py={1}>
        <Divider />
      </Stack>
      <KeyValuePair
        label="Use of Proceeds"
        value={
          latestFilling?.useOfProceeds?.length
            ? latestFilling.useOfProceeds
                .map(useOfProceed => useOfProceedsLabels[useOfProceed])
                .join(', ')
            : '-'
        }
      />
    </Section>
  );
}
